import React, { useState, useEffect, useCallback } from "react";
import ChatTabs from "./ChatTabs";
import { getApiUrl } from "../config/api";
import { useWebSocket } from "../context/WebSocketContext";
import { formatDateTime } from "../utils/dateUtils";

function capitalize(str) {
  if (!str) return "";
  // Replace dashes with spaces, then capitalize
  return str.replace(/-/g, ' ').charAt(0).toUpperCase() + str.replace(/-/g, ' ').slice(1);
}

export default function Chat({ height, isCollapsed, onToggle, onDragStart, allDealers = [], clientId, clientName, activeRfq, outstandingRfqs = [], onChatOpened, activeTab }) {
  const [tabs, setTabs] = useState([]);
  const [activeTabId, setActiveTabId] = useState(null);
  const [messagesByDealer, setMessagesByDealer] = useState({});
  const [draft, setDraft] = useState("");
  const { subscribe, sendMessage: wsSendMessage, connectionStatus } = useWebSocket();

  // Expose method to open chat for a specific RFQ
  useEffect(() => {
    if (onChatOpened && activeRfq?.rfqNumber) {
      // Check if we need to open a new tab for this RFQ
      const existingTab = tabs.find(t => t.rfqNumber === activeRfq.rfqNumber);
      if (existingTab) {
        // Tab already exists, just activate it
        setActiveTabId(existingTab.id);
      } else {
        // Need to create new tab - find dealer info from outstanding RFQs
        const rfqInfo = outstandingRfqs.find(r => r.rfqNumber === activeRfq.rfqNumber);
        
        // Determine the display name - use dealer/company name from the RFQ
        // The "dealer" field is typically the company name, not the specific dealer person
        const companyName = rfqInfo?.dealerName || activeRfq.dealerName || activeRfq.dealer || 'Company';
        
        if (rfqInfo) {
          const dealer = {
            id: rfqInfo.dealerId || rfqInfo.rfqNumber,
            name: companyName,
            location: '',
            material: rfqInfo.material || activeRfq.material,
            subMaterial: rfqInfo.subMaterial || activeRfq.subMaterial,
          };
          addChatForDealer(dealer, activeRfq.rfqNumber);
        } else {
          // Fallback: use data from activeRfq itself
          const dealer = {
            id: activeRfq.dealerId || activeRfq.rfqNumber,
            name: companyName,
            location: '',
            material: activeRfq.material,
            subMaterial: activeRfq.subMaterial,
          };
          addChatForDealer(dealer, activeRfq.rfqNumber);
        }
      }
    }
  }, [activeRfq, onChatOpened]); // eslint-disable-line react-hooks/exhaustive-deps

  const addChatForDealer = (dealer, rfqNumber) => {
    if (!dealer || !rfqNumber) {
      console.warn('[Chat] Cannot add chat - missing dealer or RFQ number', { dealer, rfqNumber });
      return;
    }
    
    setTabs((prev) => {
      if (prev.some((t) => t.id === dealer.id)) {
        setActiveTabId(dealer.id);
        return prev;
      }
      
      const newTab = {
        id: dealer.id,
        dealerId: dealer.id,
        dealerName: dealer.name || 'Company',
        dealerLocation: dealer.location || '',
        rfqNumber: rfqNumber,
        material: dealer.material || '',
        subMaterial: dealer.subMaterial || '',
      };
      
      const newTabs = [...prev, newTab];
      setActiveTabId(dealer.id);
      
      // Load existing messages for this RFQ
      loadMessagesForRfq(rfqNumber, dealer.id);
      
      return newTabs;
    });
    setMessagesByDealer((prev) => ({ ...prev, [dealer.id]: prev[dealer.id] || [] }));
  };

  // Load existing messages for an RFQ from the REST API
  const loadMessagesForRfq = async (rfqNumber, dealerId) => {
    try {
      const url = getApiUrl(`/api/chat/${rfqNumber}/messages?userId=${clientId}&userType=client`);
      const response = await fetch(url);
      
      if (!response.ok) {
        return;
      }
      
      const data = await response.json();
      
      // Parse messages from response: {"rfqNumber":"...","messages":[...]}
      const messages = (data.messages || []).map((msg, idx) => ({
        id: `${msg.senderId}-${msg.timestamp || idx}`,
        from: msg.senderType === "client" ? "me" : "dealer",
        text: msg.content || "",
        time: msg.timestamp || new Date().toISOString(),
        senderName: msg.senderName || "",
        senderId: msg.senderId,
        senderType: msg.senderType,
      }));
      
      setMessagesByDealer((prev) => ({
        ...prev,
        [dealerId]: messages,
      }));
    } catch (error) {
      console.error(`[Chat] Error loading messages for RFQ ${rfqNumber}:`, error);
    }
  };

  const closeTab = (id) => {
    setTabs((prev) => {
      const idx = prev.findIndex((t) => t.id === id);
      const newTabs = prev.filter((t) => t.id !== id);
      if (activeTabId === id) {
        const next = newTabs[Math.max(0, idx - 1)]?.id || null;
        setActiveTabId(next);
      }
      return newTabs;
    });
  };

  // Subscribe to WebSocket messages for all tabs using shared connections
  useEffect(() => {
    const unsubscribers = [];
    
    tabs.forEach((tab) => {
      const handleMessage = (data) => {
        // Parse the message format: {"senderId":123,"senderType":"client","senderName":"John Smith","content":"Hello"}
        const msg = {
          id: `${data.senderId}-${Date.now()}`,
          from: data.senderType === "client" ? "me" : "dealer",
          text: data.content || "",
          time: new Date().toISOString(),
          senderName: data.senderName || "",
          senderId: data.senderId,
          senderType: data.senderType,
        };
        
        setMessagesByDealer((prev) => ({
          ...prev,
          [tab.dealerId]: [...(prev[tab.dealerId] || []), msg],
        }));
      };
      
      // Subscribe to shared WebSocket connection
      const unsubscribe = subscribe(tab.rfqNumber, handleMessage);
      unsubscribers.push(unsubscribe);
    });
    
    // Cleanup subscriptions when tabs change
    return () => {
      unsubscribers.forEach(unsub => unsub());
    };
  }, [tabs, subscribe]);

  const send = useCallback(() => {
    const text = draft.trim();
    if (!activeTabId || !text) return;

    // Find the RFQ number for the active tab
    const activeTab = tabs.find(t => t.id === activeTabId);
    if (!activeTab?.rfqNumber) {
      console.warn(`[Chat] No RFQ number for active tab ${activeTabId}`);
      return;
    }

    // Send via shared WebSocket in the format: {"senderId":123,"senderType":"client","senderName":"John Smith","content":"Hello"}
    const payload = {
      senderId: clientId,
      senderType: "client",
      senderName: clientName,
      content: text,
    };
    
    const success = wsSendMessage(activeTab.rfqNumber, payload);
    if (success) {
      setDraft("");
    } else {
      console.warn(`[Chat] Failed to send message for dealer ${activeTabId}`);
    }
  }, [draft, activeTabId, tabs, clientId, clientName, wsSendMessage]);

  const activeMessages = messagesByDealer[activeTabId] || [];
  const activeDealer = tabs.find((t) => t.id === activeTabId);

  return (
    <>
      {!isCollapsed && (
        <div
          className="h-1 box-border bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 border-b border-gray-300 dark:border-gray-600 cursor-ns-resize transition-colors select-none"
          onMouseDown={onDragStart}
        />
      )}
      
      <div 
        className={`bg-gray-300 dark:bg-gray-800 border-t border-gray-400 dark:border-gray-700 overflow-hidden select-none relative ${isCollapsed ? 'border-b border-gray-400 dark:border-gray-700' : ''}`}
        style={{ 
          height: isCollapsed ? '40px' : `${height}px`,
          maxHeight: isCollapsed ? undefined : `${window.innerHeight / 2}px`
        }}
      >
        <button
          onClick={onToggle}
          className="absolute top-2 right-2 p-1 bg-gray-500 dark:bg-gray-700 hover:bg-gray-600 dark:hover:bg-gray-600 text-white rounded transition-colors"
          title={isCollapsed ? "Expand Chat" : "Collapse Chat"}
        >
          <svg 
            className={`w-4 h-4 transition-transform duration-200 ${isCollapsed ? 'rotate-180' : ''}`}
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
          </svg>
        </button>

        {!isCollapsed && (
          <div className="h-full flex flex-col">
            {/* Tabs area should not shrink when the chat panel height is reduced.
                Make the tabs container non-shrinkable and give it a safe min-height
                so the tab UI remains usable while the messages area will shrink/scroll. */}
            <div className="flex-shrink-0 -mb-px border-b border-gray-700">
              <ChatTabs
                tabs={tabs}
                activeTabId={activeTabId}
                onTabClick={setActiveTabId}
                onTabClose={closeTab}
                wsStatus={connectionStatus}
                allDealers={allDealers}
                onAddDealer={addChatForDealer}
                activeRfq={activeRfq}
                outstandingRfqs={outstandingRfqs}
              />
            </div>

            {/* Black section: messages + composer */}
            <div className="flex-1 flex flex-col bg-black dark:bg-gray-950 text-white min-h-0">
              {/* Messages area */}
              <div className="flex-1 overflow-y-auto px-4 py-3 min-h-0 chat-scrollbar">
                {tabs.length === 0 && (
                  <div className="text-sm text-gray-300">
                    No chats yet. Click "+" to start a conversation with a dealer.
                  </div>
                )}
                {activeTabId && (
                  <>
                    <div className="text-xs text-gray-400 mb-2">
                      Chatting with {activeDealer?.dealerName}
                      {activeDealer?.dealerLocation ? ` • ${activeDealer.dealerLocation}` : ""}
                      {activeDealer?.rfqNumber && (
                        <div className="mt-1">
                          RFQ: {activeDealer.rfqNumber}
                          {activeDealer?.material && ` • ${capitalize(activeDealer.material)}`}
                          {activeDealer?.subMaterial && ` (${capitalize(activeDealer.subMaterial)})`}
                        </div>
                      )}
                    </div>
                    <div className="space-y-2 select-text">
                      {activeMessages.map((m) => (
                        <div key={m.id} className="flex">
                          <div
                            className={`px-3 py-2 rounded max-w-[75%] ${
                              m.from === "me" ? "ml-auto bg-blue-600 text-white" : "bg-gray-800 text-gray-100"
                            }`}
                            title={formatDateTime(m.time)}
                          >
                            {m.text}
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>

              {/* Composer */}
              <div className="p-3 border-t border-gray-700 dark:border-gray-800 bg-gray-900 dark:bg-gray-950">
                <div className="flex items-center gap-2">
                  <input
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        send();
                      }
                    }}
                    placeholder={activeTabId ? "Type a message..." : "Select a dealer to start chatting"}
                    disabled={!activeTabId}
                    className="flex-1 px-2 py-2 rounded border border-gray-600 dark:border-gray-700 bg-gray-800 dark:bg-gray-900 text-white placeholder-gray-400 dark:placeholder-gray-500 select-text disabled:opacity-50"
                  />
                  <button
                    onClick={send}
                    disabled={!activeTabId || !draft.trim()}
                    className="px-3 py-2 bg-gray-700 dark:bg-gray-800 hover:bg-gray-600 dark:hover:bg-gray-700 text-white rounded disabled:opacity-50"
                  >
                    Send
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
